import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, Trash2, PenLine } from "lucide-react";
import { StarPicker } from "@/components/StarPicker";
import ReviewInvite from "@/components/ReviewInvite";
import NudgeList from "@/components/NudgeList";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PROPERTIES = [
  ["de-la-vie", "De La Vie Maison"],
  ["heritage", "Heritage"],
  ["hearthstead", "Hearthstead Cottage"],
];
const pname = (id) => PROPERTIES.find((p) => p[0] === id)?.[1] || id;

const inputCls =
  "w-full bg-transparent border-b border-[#f7f2e7]/30 focus:border-[#f7f2e7] outline-none py-2.5 text-sm text-[#f7f2e7] placeholder:text-[#f7f2e7]/35 transition-colors duration-300";
const labelCls = "block text-[10px] uppercase tracking-[0.25em] text-[#f7f2e7]/60 mb-1";

const ReviewRow = ({ r, onApprove, onDelete }) => (
  <div data-testid={`review-item-${r.id}`} className="border-b border-[#f7f2e7]/10 pb-4">
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0">
        <p className="text-sm">
          <span className="text-[#c9a227] mr-2">{"★".repeat(r.rating)}{"☆".repeat(5 - r.rating)}</span>
          {r.name} · {pname(r.property)}
        </p>
        <p className="text-xs text-[#f7f2e7]/70 font-light mt-2 italic">“{r.quote}”</p>
        <p className="text-[10px] text-[#f7f2e7]/45 mt-1.5">{new Date(r.created_at).toLocaleDateString()}</p>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        {!r.approved && (
          <button
            data-testid={`approve-review-${r.id}`}
            onClick={() => onApprove(r.id)}
            className="inline-flex items-center gap-1.5 bg-[#f7f2e7] text-[#16301f] text-[10px] uppercase tracking-[0.2em] px-3 py-2 hover:bg-[#c9a227] transition-colors duration-300"
          >
            <Check className="w-3.5 h-3.5" strokeWidth={2} /> Publish
          </button>
        )}
        <button
          data-testid={`delete-review-${r.id}`}
          onClick={() => onDelete(r.id)}
          aria-label="Remove review"
          className="text-[#f7f2e7]/50 hover:text-[#c9a227] transition-colors duration-300"
        >
          <Trash2 className="w-4 h-4" strokeWidth={1.5} />
        </button>
      </div>
    </div>
  </div>
);

export default function ReviewsManager({ passcode, nudgeKey }) {
  const [reviews, setReviews] = useState([]);
  const [form, setForm] = useState({ name: "", property: "de-la-vie", rating: 5, quote: "" });
  const [busy, setBusy] = useState(false);
  const headers = { "X-Upload-Passcode": passcode };

  const load = () =>
    axios.get(`${API}/reviews/all`, { headers }).then((r) => setReviews(r.data)).catch(() => setReviews([]));

  useEffect(() => {
    if (passcode) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passcode]);

  const approve = async (id) => {
    try {
      await axios.patch(`${API}/reviews/${id}/approve`, null, { headers });
      setReviews((rs) => rs.map((r) => (r.id === id ? { ...r, approved: true } : r)));
      toast.success("Review published to the website.");
    } catch {
      toast.error("Could not publish — check your passcode.");
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/reviews/${id}`, { headers });
      setReviews((rs) => rs.filter((r) => r.id !== id));
      toast.success("Review removed.");
    } catch {
      toast.error("Could not remove — check your passcode.");
    }
  };

  const add = async (e) => {
    e.preventDefault();
    if (!passcode) {
      toast.error("Enter the passcode first.");
      return;
    }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/reviews`, form, { headers });
      setReviews((rs) => [r.data, ...rs]);
      setForm({ name: "", property: "de-la-vie", rating: 5, quote: "" });
      toast.success("Review added and published.");
    } catch (err) {
      toast.error(err.response?.status === 401 ? "Wrong passcode." : "Could not add review.");
    } finally {
      setBusy(false);
    }
  };

  const pending = reviews.filter((r) => !r.approved);
  const published = reviews.filter((r) => r.approved);

  return (
    <div data-testid="reviews-manager" className="border border-[#f7f2e7]/15 bg-[#1a3324] p-6 sm:p-8 mt-8">
      <h2 className="font-display text-2xl">Guest Reviews</h2>
      <p className="text-xs text-[#f7f2e7]/60 font-light mt-2">
        Guests leave reviews at <span className="text-[#f7f2e7]">/review</span>. Nothing appears on the website until you publish it here.
        You can also add reviews yourself from Airbnb or Furnished Finder.
      </p>

      <ReviewInvite />
      <NudgeList passcode={passcode} refreshKey={nudgeKey} />

      {pending.length > 0 && (
        <div data-testid="pending-reviews" className="mt-7">
          <p className="text-[10px] uppercase tracking-[0.25em] text-[#c9a227] mb-4">Awaiting your approval ({pending.length})</p>
          <div className="space-y-5">
            {pending.map((r) => <ReviewRow key={r.id} r={r} onApprove={approve} onDelete={remove} />)}
          </div>
        </div>
      )}

      <form data-testid="owner-review-form" onSubmit={add} className="mt-8 border-t border-[#f7f2e7]/10 pt-7 space-y-6">
        <p className="text-[10px] uppercase tracking-[0.25em] text-[#f7f2e7]/60">Add a review yourself</p>
        <div className="grid sm:grid-cols-2 gap-x-8 gap-y-6">
          <div>
            <label htmlFor="or-name" className={labelCls}>Guest name</label>
            <input id="or-name" data-testid="owner-review-name-input" required maxLength={80} value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. Ariel" className={inputCls} />
          </div>
          <div>
            <label htmlFor="or-property" className={labelCls}>Residence</label>
            <select id="or-property" data-testid="owner-review-property-select" value={form.property} onChange={(e) => setForm((f) => ({ ...f, property: e.target.value }))} className={`${inputCls} cursor-pointer bg-[#1a3324]`}>
              {PROPERTIES.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
            </select>
          </div>
        </div>
        <div>
          <p className={labelCls}>Rating</p>
          <StarPicker dark value={form.rating} onChange={(rating) => setForm((f) => ({ ...f, rating }))} testId="owner-review-star-picker" />
        </div>
        <div>
          <label htmlFor="or-quote" className={labelCls}>Review</label>
          <textarea id="or-quote" data-testid="owner-review-quote-input" required minLength={3} maxLength={1200} rows={3} value={form.quote} onChange={(e) => setForm((f) => ({ ...f, quote: e.target.value }))} placeholder="Paste the guest's words" className={`${inputCls} resize-none`} />
        </div>
        <button
          data-testid="owner-review-submit-btn"
          type="submit"
          disabled={busy}
          className="w-full inline-flex items-center justify-center gap-2 bg-[#f7f2e7] text-[#16301f] text-xs uppercase tracking-[0.25em] px-6 py-4 hover:bg-[#c9a227] disabled:opacity-60 transition-colors duration-300"
        >
          <PenLine className="w-4 h-4" strokeWidth={1.5} />
          {busy ? "Adding..." : "Add & Publish"}
        </button>
      </form>

      {published.length > 0 && (
        <div data-testid="published-reviews" className="mt-8 border-t border-[#f7f2e7]/10 pt-7">
          <p className="text-[10px] uppercase tracking-[0.25em] text-[#f7f2e7]/60 mb-4">Live on the website ({published.length})</p>
          <div className="space-y-5">
            {published.map((r) => <ReviewRow key={r.id} r={r} onApprove={approve} onDelete={remove} />)}
          </div>
        </div>
      )}
    </div>
  );
}
